<template>
  <section class="device-status-container" :class="{ 'has-scan-column': canShowScanColumn }">
    <div class="header">
      <div class="header-left">
      <h4>{{$t('device_status')}}</h4>
      </div>
      <div class="header-right">
      <transition name="fade">
          <div class="search-container-wrapper">
          <TableSearch :tableTitles="tableTitles" @input="handleInput" />
        </div>
      </transition>
        <DeviceActionsDropdown
          v-if="canShowActionsColumn"
          ref="deviceActionsDropdown"
          :isSiteActionFlags="isSiteActionFlags"
          :siteActionFlags="siteActionFlags"
          :isMobile="isMobile"
          :isDropdownOpen="isDropdownOpen"
          @toggle-dropdown="toggleDropdown"
          @action-click="handleActionClick"
          @toggle-flag="toggleSiteActionFlag"
        />
      </div>
    </div>

    <!-- Statistics Dashboard -->
    <DeviceStatusStats
      :devicesStatus="devicesStatus"
      :totalDevices="totalDevices"
      :workingDevices="workingDevices"
      :warningDevices="warningDevices"
      :offlineDevices="offlineDevices"
      :workingPercentage="workingPercentage"
      :warningPercentage="warningPercentage"
      :offlinePercentage="offlinePercentage"
      @show-working="showWorkingDevices"
      @show-offline="showOfflineDevices"
    />

    <Table
      v-if="devicesToShow.length"
      :key="componentKey"
      :tableData="devicesToShow"
      :isHidingRowIndex="isMobile || canShowScanColumn"
      class="shift-params-table"
      headerFontSize="13px"
      bodyFontSize="15px"
      idField="id"
      :isNested="false"
      :customRowGridStr="gridStyle"
      :sortComparators="sortComparators"
      @cellClicked="cellClick"
      @setActionFlags="setActionFlags"
      @toggleFlag="toggleFlag"
    />
  </section>
</template>

<script>
import Table from "@/modules/common/components/customTable/Table"
import util from '../../common/services/utilService';
import { mapGetters } from "vuex"
import { tabletAction } from '../services/deviceService'
import sensorService from '../../control-panel/services/sensorService'
import deviceManagerService from '../../control-panel/services/deviceManagerService'
import { ddmmyyhhmm } from '../../../filters'
import swalService from "@/modules/common/services/swalService"
import TableSearch from '@/modules/common/components/customTable/TableSearch'
import Swal from "sweetalert2"
import DeviceActionsDropdown from './DeviceActionsDropdown'
import DeviceStatusStats from './DeviceStatusStats'
import { calculateAllStats, getDevicesByStatus } from '../utils/deviceStatsUtils'
import {
	appendScanDeviceCell,
	canSeeDeviceActions,
	canShowPadScanColumn,
	getDeviceStatusGridStyle,
	SCAN_DEVICE_QR_TYPE,
} from '../utils/deviceCodeUtils'
import { compareRoomLocation } from '../utils/roomLocationSortUtils'

export default {
  props: {
    devicesStatus: { type: Array, required: true }
  },
  data() {
    return {
      currDevice: null,
      searchBy: '',
      searchTxt: '',
      siteActionFlags: {},
      isSiteActionFlags: false,
      componentKey: 0,
      tableTitles: [],
      isMobile: false,
      isDropdownOpen: false,
      expandedCards: new Set(),
    }
  },
  created() {
    this.setSiteActionFlags()
    this.setTableTitles()
    this.isMobile = window.innerWidth <= 820
    window.addEventListener('resize', this.handleResize)
    document.addEventListener('click', this.handleDocumentClick)
  },
  mounted() {
    this.isMobile = window.innerWidth <= 820
    this.$nextTick(() => {
      this.addDataLabelsToTableCells()
      
      if (this.isMobile) {
        this.setupMobileDropdownButton()
        setTimeout(() => {
          this.setupMobileCardExpansion()
          this.updateCardExpansionState()
        }, 100)
      }
    })
  },
  updated() {
    this.$nextTick(() => {
      this.addDataLabelsToTableCells()
      
      if (this.isMobile && this.isSiteActionFlags && this.$refs.dropdownButton && !this.$refs.dropdownButton.hasAttribute('data-mobile-setup')) {
        this.setupMobileDropdownButton()
      }
      
      if (this.isMobile) {
        this.setupMobileCardExpansion()
        this.updateCardExpansionState()
      }
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('click', this.handleDocumentClick)
  },
  methods: {
    forceRerender() {
      this.componentKey += 1
    },
    ensureDeviceNote(device) {
      if (!device) return
      if (typeof device.note === 'string') {
        try {
          device.note = JSON.parse(device.note)
        } catch {
          device.note = { txt: '', date: ddmmyyhhmm(new Date()), user_info: '' }
        }
      }
      if (!device.note) {
        device.note = {
          txt: '',
          date: ddmmyyhhmm(new Date()),
          user_info: `${this.loggedUser?.first_name || ''} ${this.loggedUser?.last_name || ''}`.trim(),
        }
      }
    },
    async cellClick(payload) {
      if (!payload) return
      if (payload.type === SCAN_DEVICE_QR_TYPE) {
        this.$emit('scanDevice', payload.deviceCode)
        return
      }
      if (payload.icon && payload.icon.includes('errors_list')) {
        await this.setErrorsList(payload)
        this.$emit('cellClick', this.currDevice, 'errors')
        return
      }
      if (payload?.type === 'battery' && payload.deviceCode) {
        const device = await sensorService.getByDeviceCode(payload.deviceCode)
        this.ensureDeviceNote(device)
        this.$emit('cellClick', device, 'battery')
        return
      }
      const { id, icon, type, deviceCode, appType } = payload
      if (icon && !type && deviceCode) {
        this.currDevice = await sensorService.getByDeviceCode(deviceCode)
        const { note } = this.currDevice
        if (!note || note && !note.txt) {
          this.currDevice.note = {
            txt: '',
            date: ddmmyyhhmm(new Date()),
            user_info: `${this.loggedUser.first_name} ${this.loggedUser.last_name}`
          }
        }
        this.$emit('cellClick', this.currDevice, 'note')
      } else {
        tabletAction(type, id, appType)
      }
    },
    async setActionFlags(actionFlags) {
      if (!actionFlags?.id) {
        swalService.errorMsg()
        return
      }
      try {
        await deviceManagerService.setActionFlags(actionFlags)
        const { site: siteId } = this.$store.getters.filterSelected
        await this.$store.dispatch({ type: 'getDevices', siteId })
        this.forceRerender()
      } catch (err) {
        swalService.errorMsg()
      }
    },
    async setActionFlagsBySiteId(actionFlags, siteId) {
      if (!actionFlags) return
      try {
        await deviceManagerService.setActionFlagsBySiteId(actionFlags, siteId)
        await this.$store.dispatch({ type: "getDevices", siteId })
        this.forceRerender()
      } catch (err) {
        swalService.errorMsg()
      }
    },
    async toggleFlag(flag, actionFlags) {
      if (!actionFlags) return
      const { name, isOn } = flag
      const { user_flags } = actionFlags
      if (name in user_flags) user_flags[name] = isOn
      else actionFlags[name] = +isOn

      if (actionFlags.id) {
        await this.setActionFlags(actionFlags)
      }
    },
    toggleSiteActionFlag(flag) {
      this.toggleFlag(flag, this.siteActionFlags)
      const { site: siteId } = this.$store.getters.filterSelected
      this.setActionFlagsBySiteId(this.siteActionFlags, siteId)
    },
    setSiteActionFlags() {
      this.siteActionFlags = deviceManagerService.getDefaultActionFlags()
      if (!this.devicesStatus || !Array.isArray(this.devicesStatus) || this.devicesStatus.length === 0) {
        return
      }
      let { user_flags, sensor_flags } = this.siteActionFlags
      this.devicesStatus.forEach(device => {
        const { actions } = device
        const isActionFlags = !Array.isArray(actions) && typeof actions === 'object'
        if (!isActionFlags) return
        if (actions.is_update) this.siteActionFlags.is_update = true
        if (actions.is_restart) this.siteActionFlags.is_restart = true
        if (actions.user_flags && actions.user_flags.is_send_data === false) user_flags.is_send_data = false
        if (actions.user_flags && actions.user_flags.is_send_count === false) user_flags.is_send_count = false
        if (actions.user_flags && actions.user_flags.is_blink === true) user_flags.is_blink = true
        if (actions.user_flags && actions.user_flags.is_like_btn === false) user_flags.is_like_btn = false
        if (actions.user_flags && actions.user_flags.is_maintenance_btn === true) user_flags.is_maintenance_btn = true
        if (actions.user_flags && actions.user_flags.is_beacon === false) user_flags.is_beacon = false
        if (actions.sensor_flags && actions.sensor_flags.is_send_data === false) sensor_flags.is_send_data = false
        if (actions.sensor_flags && actions.sensor_flags.is_send_count === false) sensor_flags.is_send_count = false
        if (actions.sensor_flags && actions.sensor_flags.is_blink === true) sensor_flags.is_blink = true
        if (actions.sensor_flags && actions.sensor_flags.is_like_btn === false) sensor_flags.is_like_btn = false
        if (actions.sensor_flags && actions.sensor_flags.is_maintenance_btn === true) sensor_flags.is_maintenance_btn = true
        if (actions.sensor_flags && actions.sensor_flags.is_beacon === false) sensor_flags.is_beacon = false

        this.isSiteActionFlags = true
      })
    },
    async setErrorsList({ deviceCode }) {
      const currDevice = await sensorService.getByDeviceCode(deviceCode)
      const latestErrorsInfo = await sensorService.getSensorErrors(deviceCode)
      if (latestErrorsInfo.length === 0) currDevice.latest_errors = [this.$t('no_errors')]
      if (latestErrorsInfo.length > 0) {
        currDevice.latest_errors = latestErrorsInfo.map(error => {
            return `${ddmmyyhhmm(error.date)}: ${error.id} - ${error.name.toLowerCase()}`
        })
      }
      this.currDevice = currDevice
      return currDevice.latest_errors
    },
    setTableTitles() {
      if (!this.devicesStatus || !Array.isArray(this.devicesStatus) || this.devicesStatus.length === 0 || !this.devicesStatus[0]) {
        this.tableTitles = []
        return
      }
      this.tableTitles = Object.keys(this.devicesStatus[0]).filter(key => {
        return !['actions', 'battery', 'counter_status', 'date', 'id', 'latest_errors', 'status', 'version', 'site_id', 'room_id', 'building', 'floor'].includes(key)
      })
      if (this.canShowScanColumn) {
        this.tableTitles.push('scan_device')
      }
    },
    handleInput(value, searchBy) {
      if (searchBy) this.searchBy = searchBy
      this.searchTxt = value
    },
    handleResize() {
      const wasMobile = this.isMobile
      this.isMobile = window.innerWidth <= 820
      
      if (this.isMobile && !wasMobile) {
        this.expandedCards.clear()
      }
      
      this.$nextTick(() => {
        this.addDataLabelsToTableCells()
        if (this.isMobile) {
          this.setupMobileCardExpansion()
          this.updateCardExpansionState()
        }
      })
    },
    setupMobileCardExpansion() {
      if (!this.isMobile) return
      
      setTimeout(() => {
        const tableRows = document.querySelectorAll('.shift-params-table .table-row:not(.table-head .table-row)')
        
        if (tableRows.length === 0) {
          this.$nextTick(() => {
            this.setupMobileCardExpansion()
          })
          return
        }
        
        tableRows.forEach((row, index) => {
          if (row.hasAttribute('data-mobile-expansion-setup')) return
          
          row.setAttribute('data-mobile-expansion-setup', 'true')
          
          const deviceId = this.getDeviceIdFromRow(row, index)
          
          if (!row.getAttribute('data-device-id')) {
            row.setAttribute('data-device-id', deviceId)
          }
          
          const firstCell = row.querySelector('.table-cell:not(.cell-icon):not(.checkbox-container):not(.triangle-container)')
          if (firstCell && !firstCell.querySelector('.mobile-expand-arrow')) {
            const isRTL = this.isRTL
            const expandArrow = document.createElement('div')
            expandArrow.className = 'mobile-expand-arrow'
            expandArrow.setAttribute('data-device-id', deviceId)
            expandArrow.innerHTML = isRTL ? '◀' : '▶'
            
            if (isRTL) {
              expandArrow.style.cssText = 'position: absolute; right: 15px; top: 50%; transform: translateY(-50%); font-size: 16px; color: #666; cursor: pointer; user-select: none; z-index: 10; transition: transform 0.3s ease; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;'
              firstCell.style.paddingRight = '50px'
            } else {
              expandArrow.style.cssText = 'position: absolute; left: 15px; top: 50%; transform: translateY(-50%); font-size: 16px; color: #666; cursor: pointer; user-select: none; z-index: 10; transition: transform 0.3s ease; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;'
              firstCell.style.paddingLeft = '50px'
            }
            
            const handleArrowClick = (e) => {
              e.stopPropagation()
              e.stopImmediatePropagation()
              e.preventDefault()
              this.toggleCardExpansion(deviceId)
            }
            
            expandArrow.addEventListener('click', handleArrowClick, true)
            expandArrow.addEventListener('touchend', (e) => {
              e.stopPropagation()
              e.stopImmediatePropagation()
              e.preventDefault()
              this.toggleCardExpansion(deviceId)
            }, { passive: false })
            
            firstCell.style.position = 'relative'
            firstCell.appendChild(expandArrow)
          }
          
          row.style.cursor = 'default'
        })
      }, 50)
    },
    getDeviceIdFromRow(row, index) {
      const deviceIdAttr = row.getAttribute('data-device-id')
      if (deviceIdAttr) return deviceIdAttr
      
      const deviceIdCell = row.querySelector('[data-label*="device_id" i], [data-label*="Device ID" i]')
      if (deviceIdCell) {
        const deviceId = deviceIdCell.textContent.trim()
        if (deviceId) {
          row.setAttribute('data-device-id', deviceId)
          return deviceId
        }
      }
      
      const firstCell = row.querySelector('.table-cell:not(.cell-icon):not(.checkbox-container):not(.triangle-container)')
      if (firstCell) {
        const deviceId = firstCell.textContent.trim()
        if (deviceId) {
          row.setAttribute('data-device-id', deviceId)
          return deviceId
        }
      }
      
      const fallbackId = `device-${index}`
      row.setAttribute('data-device-id', fallbackId)
      return fallbackId
    },
    toggleCardExpansion(deviceId) {
      if (this.expandedCards.has(deviceId)) {
        this.expandedCards.delete(deviceId)
      } else {
        this.expandedCards.add(deviceId)
      }
      
      this.$forceUpdate()
      this.$nextTick(() => {
        this.updateCardExpansionState()
      })
    },
    updateCardExpansionState() {
      if (!this.isMobile) return
      
      this.$nextTick(() => {
        const tableRows = document.querySelectorAll('.shift-params-table .table-row:not(.table-head .table-row)')
        
        tableRows.forEach((row) => {
          const deviceId = row.getAttribute('data-device-id')
          if (!deviceId) {
            const firstCell = row.querySelector('.table-cell:not(.cell-icon):not(.checkbox-container):not(.triangle-container)')
            if (firstCell) {
              const deviceIdText = firstCell.textContent.trim()
              if (deviceIdText) {
                row.setAttribute('data-device-id', deviceIdText)
                const newDeviceId = deviceIdText
                const isExpanded = this.expandedCards.has(newDeviceId)
                row.classList.toggle('mobile-expanded', isExpanded)
                row.classList.toggle('mobile-collapsed', !isExpanded)
                this.updateRowCells(row, isExpanded)
              }
            }
            return
          }
          
          const isExpanded = this.expandedCards.has(deviceId)
          row.classList.remove('mobile-expanded', 'mobile-collapsed')
          if (isExpanded) {
            row.classList.add('mobile-expanded')
          } else {
            row.classList.add('mobile-collapsed')
          }
          
          const expandArrow = row.querySelector('.mobile-expand-arrow')
          if (expandArrow) {
            const isRTL = this.isRTL
            if (isExpanded) {
              expandArrow.innerHTML = '▼'
              expandArrow.style.transform = 'translateY(-50%) rotate(180deg)'
            } else {
              expandArrow.innerHTML = isRTL ? '◀' : '▶'
              expandArrow.style.transform = 'translateY(-50%) rotate(0deg)'
            }
          }
          
          this.updateRowCells(row, isExpanded)
        })
      })
    },
    updateRowCells(row, isExpanded) {
      const cells = Array.from(row.querySelectorAll('.table-cell:not(.cell-icon):not(.checkbox-container):not(.triangle-container)'))
      
      if (this.isMobile && !isExpanded) {
        const tooltips = row.querySelectorAll('.mobile-tooltip')
        tooltips.forEach(tooltip => {
          tooltip.style.setProperty('display', 'none', 'important')
          tooltip.style.setProperty('visibility', 'hidden', 'important')
          tooltip.style.setProperty('opacity', '0', 'important')
        })
      }
      
      if (cells.length > 0) {
        const deviceIdCell = cells.find(cell => {
          const label = cell.getAttribute('data-label') || ''
          const cellText = cell.textContent || ''
          return label.toLowerCase().includes('device_id') || 
                 label.toLowerCase().includes('device id') ||
                 cellText.toLowerCase().includes('device_id')
        })
        const batteryCell = cells.find(cell => {
          return cell.classList.contains('battery-cell') || 
                 cell.querySelector('.battery-container') ||
                 (cell.getAttribute('data-label') || '').toLowerCase().includes('battery')
        })
        
        cells.forEach((cell, index) => {
          if (isExpanded) {
            cell.classList.remove('mobile-hidden', 'mobile-summary-cell')
            cell.style.display = 'flex'
          } else {
            const isDeviceId = cell === deviceIdCell
            const isBattery = cell === batteryCell
            const isFirst = index === 0
            
            if (isFirst || isDeviceId || isBattery) {
              cell.classList.remove('mobile-hidden')
              if (isFirst) {
                cell.classList.add('mobile-summary-cell')
              }
              cell.style.display = 'flex !important'
              cell.style.setProperty('display', 'flex', 'important')
            } else {
              cell.classList.add('mobile-hidden')
              cell.classList.remove('mobile-summary-cell')
              cell.style.display = 'none'
            }
          }
        })
      }
    },
    addDataLabelsToTableCells() {
      if (!this.devicesToShow || !this.devicesToShow.length) return
      
      this.$nextTick(() => {
        const firstDevice = this.devicesToShow[0]
        const columnKeys = Object.keys(firstDevice).filter(key => {
          return key !== 'id' && firstDevice[key] !== undefined
        })
        
        const getLabelForKey = (key) => {
          const translations = {
            'device_id': 'device_id',
            'room_name': 'room_name',
            'version': 'version',
            'status': 'status',
            'counter_status': 'counter_status',
            'battery': 'battery',
            'device_type': 'device_type'
          }
          
          if (translations[key]) {
            const translated = this.$t(translations[key])
            return translated !== translations[key] ? translated : key.split('_').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ')
          }
          
          const translated = this.$t(key)
          return translated !== key ? translated : key.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ')
        }
        
        const columnLabels = columnKeys.map(key => getLabelForKey(key))
        
        const tableRows = document.querySelectorAll('.shift-params-table .table-row:not(.table-head .table-row)')
        if (!tableRows.length) return
        
        const headerRow = document.querySelector('.shift-params-table .table-head .table-row')
        let headerOrder = [...columnLabels]
        
        if (headerRow) {
          const headerCells = headerRow.querySelectorAll('.table-cell:not(.cell-icon):not(.checkbox-container):not(.triangle-container)')
          headerOrder = Array.from(headerCells).map((cell, index) => {
            const text = cell.textContent.trim()
            if (text) {
              const translated = this.$t(text)
              if (translated !== text) {
                return translated
              }
            }
            return columnLabels[index] || text || getLabelForKey(columnKeys[index])
          })
        }
        
        const actionLabels = {
          'edit-icon': this.$t('edit') || 'Edit',
          'delete-icon': this.$t('delete') || 'Delete',
          'errors_list': this.$t('errors') || 'Errors',
          'restart': this.$t('restart') || 'Restart',
          'update': this.$t('update') || 'Update'
        }
        
        tableRows.forEach((row, rowIndex) => {
          const existingWrappers = row.querySelectorAll('.merged-icon-wrapper')
          existingWrappers.forEach(wrapper => wrapper.remove())
          
          const device = this.devicesToShow[rowIndex]
          
          const cells = row.querySelectorAll('.table-cell:not(.cell-icon):not(.checkbox-container):not(.triangle-container)')
          const allCells = Array.from(cells)
          
          allCells.forEach((cell, index) => {
            const label = headerOrder[index] || columnLabels[index] || getLabelForKey(columnKeys[index])
            if (label) {
              cell.setAttribute('data-label', label)
            }
            
            if (cell.querySelector('.battery-container')) {
              cell.classList.add('battery-cell')
            }
            
            const cellLabel = cell.getAttribute('data-label') || label || ''
            if (cellLabel.toLowerCase().includes('version')) {
              cell.classList.add('version-cell')
            }
            
            if (this.isMobile) {
              const cellLabel = cell.getAttribute('data-label') || ''
              const hasErrorsIcon = cell.querySelector('img[src*="errors_list"]')
              const cellText = cell.textContent || ''
              
              if (hasErrorsIcon || 
                  cellLabel.toLowerCase().includes('error') || 
                  cellLabel.toLowerCase().includes('latest') ||
                  cellText.toLowerCase().includes('error') ||
                  cell.classList.contains('icon-cell') && hasErrorsIcon) {
                cell.style.display = 'none'
                cell.style.visibility = 'hidden'
                return
              }
              
              if (cell.classList.contains('icon-cell') || cell.querySelector('img')) {
                const icon = cell.querySelector('img')
                if (icon) {
                  const iconSrc = icon.getAttribute('src') || ''
                  const isDeviceInfoIcon = iconSrc.includes('device_info')
                  
                  if (isDeviceInfoIcon) {
                    const versionCell = allCells.find(c => {
                      const label = c.getAttribute('data-label') || ''
                      return label.toLowerCase().includes('version') || c.classList.contains('version-cell')
                    })
                    
                    if (versionCell) {
                      const existingIcon = versionCell.querySelector(`img[src*="device_info"]`)
                      if (existingIcon) {
                        cell.style.display = 'none'
                        return
                      }
                      
                      const tooltipTexts = device && device.version && device.version.tooltip ? device.version.tooltip.texts : null
                      
                    const iconWrapper = document.createElement('div')
                      iconWrapper.className = 'merged-icon-wrapper tooltip'
                      const isRTL = this.isRTL
                      if (isRTL) {
                        iconWrapper.style.cssText = 'margin-right: auto; display: flex; align-items: center; flex-shrink: 0; position: relative;'
                      } else {
                        iconWrapper.style.cssText = 'margin-left: auto; display: flex; align-items: center; flex-shrink: 0; position: relative;'
                      }
                      
                    const iconClone = icon.cloneNode(true)
                    iconClone.style.cssText = 'width: 24px; height: 24px; cursor: pointer;'
                    
                      if (tooltipTexts && tooltipTexts.length > 0) {
                        const tooltipDiv = document.createElement('div')
                        tooltipDiv.className = 'mobile-tooltip top'
                        const isCurrentlyMobile = window.innerWidth <= 820
                        if (isCurrentlyMobile) {
                          tooltipDiv.style.cssText = 'top: calc(100% + 8px); left: auto; right: 0; max-width: min(320px, calc(100vw - 32px)); transform: none; padding: 10px; color: #2c3e50; background-color: #f5f5f5; font-weight: normal; font-size: 12px; border-radius: 8px; position: absolute; box-sizing: border-box; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3); display: none; z-index: 9998; white-space: pre-line; word-break: break-word; pointer-events: none;'
                      } else {
                          tooltipDiv.style.cssText = 'top: -10px; left: 50%; min-width: max-content; transform: translate(-50%, -100%); padding: 10px; color: #2c3e50; background-color: #f5f5f5; font-weight: normal; font-size: 12px; border-radius: 8px; position: absolute; box-sizing: border-box; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3); display: none; z-index: 9998; white-space: pre-line; pointer-events: none;'
                        }
                        tooltipDiv.setAttribute('data-tooltip-device-id', device ? (device.device_id || rowIndex) : rowIndex)
                        
                        const textsContainer = document.createElement('div')
                        textsContainer.style.cssText = 'display: flex; flex-direction: column; gap: 5px;'
                        
                        tooltipTexts.forEach(text => {
                          const textDiv = document.createElement('div')
                          textDiv.textContent = text
                          textDiv.style.cssText = 'white-space: pre-line; word-break: break-word;'
                          textsContainer.appendChild(textDiv)
                        })
                        
                        // Add tooltip arrow
                        const arrow = document.createElement('i')
                        const arrowTop = isCurrentlyMobile ? '-15px' : '100%'
                        const arrowLeft = isCurrentlyMobile ? 'auto' : '50%'
                        const arrowRight = isCurrentlyMobile ? '12px' : 'auto'
                        arrow.style.cssText = `position: absolute; top: ${arrowTop}; left: ${arrowLeft}; right: ${arrowRight}; margin-left: ${isCurrentlyMobile ? '0' : '-15px'}; width: 30px; height: 15px; overflow: hidden; pointer-events: none;`
                        const arrowInner = document.createElement('div')
                        arrowInner.style.cssText = isCurrentlyMobile
                          ? 'position: absolute; width: 15px; height: 15px; left: 50%; bottom: 0; top: auto; transform: translate(-50%, 50%) rotate(45deg); background-color: #f5f5f5; box-shadow: 0 1px 8px rgba(0, 0, 0, 0.3);'
                          : 'position: absolute; width: 15px; height: 15px; left: 50%; top: 0; transform: translate(-50%, -50%) rotate(45deg); background-color: #f5f5f5; box-shadow: 0 1px 8px rgba(0, 0, 0, 0.3);'
                        arrow.appendChild(arrowInner)
                        
                        tooltipDiv.appendChild(textsContainer)
                        tooltipDiv.appendChild(arrow)
                    iconWrapper.appendChild(iconClone)
                        iconWrapper.appendChild(tooltipDiv)
                        
                        if (isCurrentlyMobile) {
                          tooltipDiv.style.display = 'none'
                          tooltipDiv.style.visibility = 'hidden'
                          tooltipDiv.style.opacity = '0'
                          
                          const showTooltipOnHover = () => {
                            if (!wasOpenedByClick) {
                              tooltipDiv.style.setProperty('display', 'block', 'important')
                              tooltipDiv.style.setProperty('visibility', 'visible', 'important')
                              tooltipDiv.style.setProperty('opacity', '1', 'important')
                              isTooltipVisible = true
                            }
                          }
                          const hideTooltipOnHover = () => {
                            if (!wasOpenedByClick) {
                              tooltipDiv.style.setProperty('display', 'none', 'important')
                              tooltipDiv.style.setProperty('visibility', 'hidden', 'important')
                              tooltipDiv.style.setProperty('opacity', '0', 'important')
                              isTooltipVisible = false
                            }
                          }
                          
                          iconWrapper.addEventListener('mouseenter', showTooltipOnHover)
                          iconWrapper.addEventListener('mouseleave', hideTooltipOnHover)
                          iconClone.addEventListener('mouseenter', showTooltipOnHover)
                          iconClone.addEventListener('mouseleave', hideTooltipOnHover)
                          versionCell.addEventListener('mouseenter', showTooltipOnHover)
                          versionCell.addEventListener('mouseleave', hideTooltipOnHover)
                          
                          let isTooltipVisible = false
                          let wasOpenedByClick = false
                          
                          const showTooltipOnClick = (e) => {
                            e.stopPropagation()
                            e.stopImmediatePropagation()
                            e.preventDefault()
                            
                            if (isTooltipVisible) {
                              tooltipDiv.style.setProperty('display', 'none', 'important')
                              tooltipDiv.style.setProperty('visibility', 'hidden', 'important')
                              tooltipDiv.style.setProperty('opacity', '0', 'important')
                              isTooltipVisible = false
                              wasOpenedByClick = false
                            } else {
                              tooltipDiv.style.setProperty('display', 'block', 'important')
                              tooltipDiv.style.setProperty('visibility', 'visible', 'important')
                              tooltipDiv.style.setProperty('opacity', '1', 'important')
                              isTooltipVisible = true
                              wasOpenedByClick = true
                            }
                          }
                          
                          const hideTooltipOnOutsideClick = (e) => {
                            const clickedOutside = !versionCell.contains(e.target) && !iconWrapper.contains(e.target) && !tooltipDiv.contains(e.target)
                            if (clickedOutside && isTooltipVisible) {
                              tooltipDiv.style.setProperty('display', 'none', 'important')
                              tooltipDiv.style.setProperty('visibility', 'hidden', 'important')
                              tooltipDiv.style.setProperty('opacity', '0', 'important')
                              isTooltipVisible = false
                              wasOpenedByClick = false
                            }
                          }
                          
                          versionCell.addEventListener('click', showTooltipOnClick, true)
                          iconWrapper.addEventListener('click', showTooltipOnClick, true)
                          iconClone.addEventListener('click', showTooltipOnClick, true)
                          
                          versionCell.addEventListener('touchend', (e) => {
                            e.stopPropagation()
                            e.stopImmediatePropagation()
                            e.preventDefault()
                            showTooltipOnClick(e)
                          }, { passive: false, capture: true })
                          iconWrapper.addEventListener('touchend', (e) => {
                            e.stopPropagation()
                            e.stopImmediatePropagation()
                            e.preventDefault()
                            showTooltipOnClick(e)
                          }, { passive: false, capture: true })
                          iconClone.addEventListener('touchend', (e) => {
                            e.stopPropagation()
                            e.stopImmediatePropagation()
                            e.preventDefault()
                            showTooltipOnClick(e)
                          }, { passive: false, capture: true })
                          
                          document.addEventListener('click', hideTooltipOnOutsideClick, true)
                        } else {
                          tooltipDiv.style.display = 'none'
                          tooltipDiv.style.visibility = 'hidden'
                          tooltipDiv.style.opacity = '0'
                          iconWrapper.addEventListener('mouseenter', () => {
                            tooltipDiv.style.display = 'block'
                            tooltipDiv.style.visibility = 'visible'
                            tooltipDiv.style.opacity = '1'
                          })
                          iconWrapper.addEventListener('mouseleave', () => {
                            tooltipDiv.style.display = 'none'
                            tooltipDiv.style.visibility = 'hidden'
                            tooltipDiv.style.opacity = '0'
                          })
                        }
                        
                        versionCell.setAttribute('data-has-tooltip', 'true')
                      } else {
                        iconWrapper.appendChild(iconClone)
                        
                        const originalCell = cell
                    iconWrapper.onclick = (e) => {
                      e.stopPropagation()
                      if (originalCell.onclick) {
                        originalCell.onclick(e)
                      } else {
                        const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true })
                        originalCell.dispatchEvent(clickEvent)
                          }
                      }
                    }
                    
                      versionCell.appendChild(iconWrapper)
                    cell.style.display = 'none'
                      return
                  }
                  }
                }
              }
              
              if (index < allCells.length - 1) {
                const nextCell = allCells[index + 1]
                if (nextCell && (nextCell.classList.contains('icon-cell') || nextCell.querySelector('img'))) {
                  const nextCellLabel = nextCell.getAttribute('data-label') || ''
                  const hasErrorsIcon = nextCell.querySelector('img[src*="errors_list"]')
                  if (hasErrorsIcon || nextCellLabel.toLowerCase().includes('error') || nextCellLabel.toLowerCase().includes('latest')) {
                    nextCell.style.display = 'none'
                    return
                  }
                }
              }
            }
          })
          
          const actionIcons = row.querySelectorAll('.cell-icon.action')
          actionIcons.forEach(icon => {
            const img = icon.querySelector('img')
            if (img) {
              const src = img.getAttribute('src') || ''
              const alt = img.getAttribute('alt') || ''
              
              let label = ''
              if (src.includes('edit')) label = actionLabels['edit-icon']
              else if (src.includes('delete')) label = actionLabels['delete-icon']
              else if (src.includes('errors_list')) label = actionLabels['errors_list']
              else if (src.includes('restart')) label = actionLabels['restart']
              else if (src.includes('update')) label = actionLabels['update']
              else if (alt) {
                label = actionLabels[alt] || this.$t(alt) || alt
              }
              
              if (label) {
                icon.setAttribute('data-label', label)
              } else {
                icon.setAttribute('data-label', alt || 'Action')
              }
              
              if (this.isMobile && !icon.querySelector('.action-label-text')) {
                const labelText = document.createElement('span')
                labelText.className = 'action-label-text'
                labelText.textContent = label || alt || 'Action'
                labelText.style.cssText = 'font-size: 13px; color: #2c3e50; font-weight: 500; margin-left: 8px;'
                icon.appendChild(labelText)
              }
            }
          })
          
          const iconCells = row.querySelectorAll('.table-cell.icon-cell')
          iconCells.forEach(cell => {
            const img = cell.querySelector('img')
            if (!img) return
            
            const src = img.getAttribute('src') || ''
            const alt = img.getAttribute('alt') || ''
            
            if (this.isMobile && src.includes('errors_list')) {
              cell.style.display = 'none'
              cell.style.visibility = 'hidden'
              cell.removeAttribute('data-label')
              return
            }
            
            let label = ''
            if (src.includes('errors_list')) {
              label = this.$t('latest_errors') || 'Latest Errors'
            } else if (alt) {
              label = this.$t(alt) || alt
            }
            
            if (label && !cell.getAttribute('data-label')) {
              cell.setAttribute('data-label', label)
            }
          })
        })
      })
    },
    setupMobileDropdownButton() {
      // Wait for DOM to be ready
      setTimeout(() => {
        const button = this.$refs.dropdownButton
        if (button && !button.hasAttribute('data-mobile-setup')) {
          // Mark as setup
          button.setAttribute('data-mobile-setup', 'true')
          
          // Add click handler - use arrow function to preserve 'this'
          const handleClick = (e) => {
            e.stopPropagation()
            e.preventDefault()
            e.stopImmediatePropagation()
            
            // Toggle state directly
            this.isDropdownOpen = !this.isDropdownOpen
            
            // Show dropdown immediately
            this.$nextTick(() => {
              if (this.isDropdownOpen) {
                const dropdown = this.$refs.dropdownWrapper?.querySelector('.dropdown-menu')
                if (dropdown) {
                  dropdown.style.setProperty('display', 'block', 'important')
                  dropdown.style.setProperty('visibility', 'visible', 'important')
                  dropdown.style.setProperty('opacity', '1', 'important')
                  dropdown.style.setProperty('z-index', '9998', 'important') // Below modal (9999)
                }
              }
            })
          }
          
          // Add multiple event listeners to catch all touch/click events
          button.addEventListener('click', handleClick, true)
          button.addEventListener('touchend', handleClick, true)
          button.addEventListener('touchstart', (e) => {
            e.stopPropagation()
          }, true)
        }
      }, 500)
    },
    toggleDropdown(event) {
      // Only for desktop
      if (this.isMobile) {
        return
      }
      
      if (event) {
        event.stopPropagation()
        event.stopImmediatePropagation()
      }
      
      // Toggle the dropdown state immediately
      const newState = !this.isDropdownOpen
      this.isDropdownOpen = newState
      
      // Force Vue reactivity
      this.$forceUpdate()
    },
    closeDropdown() {
      this.isDropdownOpen = false
    },
    handleDocumentClick(event) {
      if (!this.isDropdownOpen || !this.$refs.deviceActionsDropdown) return
      
      const target = event.target
      const dropdownComponent = this.$refs.deviceActionsDropdown
      const wrapper = dropdownComponent.$el
      
      if (!wrapper) return
      
      const button = wrapper.querySelector('.dropdown-trigger')
      
      if (button && (button === target || button.contains(target))) return
      
      if (this.isMobile) {
        setTimeout(() => {
          if (wrapper && !wrapper.contains(target)) {
            this.closeDropdown()
          }
        }, 300)
      } else {
        if (wrapper && !wrapper.contains(target)) {
          this.closeDropdown()
        }
      }
    },
    handleActionClick(actionName) {
      const currentValue = this.siteActionFlags[actionName]
      this.toggleSiteActionFlag({ name: actionName, isOn: !currentValue })
    },
    calculateAllStats() {
      return calculateAllStats(this.devicesStatus)
    },
    getOfflineDevices() {
      return getDevicesByStatus(this.devicesStatus, 'offline')
    },
    getWorkingDevices() {
      return getDevicesByStatus(this.devicesStatus, 'working')
    },
    showWorkingDevices() {
      const workingDevices = this.getWorkingDevices()
      
      if (workingDevices.length === 0) {
        Swal.fire({
          icon: 'info',
          title: this.$t('working'),
          text: this.$t('no_working_devices') || 'No working devices found',
          confirmButtonColor: '#3085d6'
        })
        return
      }

      // Create table rows
      const tableRows = workingDevices.map((device, index) => {
        const isEven = index % 2 === 0
        return `
          <tr class="device-row" style="border-bottom: 1px solid #e8e8e8; transition: background 0.2s; background: ${isEven ? '#fff' : '#fafafa'};">
            <td style="padding: 14px 8px; text-align: center; color: #666; font-weight: 500; width: 50px; font-size: 13px;">${index + 1}</td>
            <td style="padding: 14px 16px; font-weight: 500; color: #2c3e50; font-size: 14px;">${device.room_name || 'N/A'}</td>
            <td style="padding: 14px 16px; color: #555; font-family: 'Courier New', monospace; font-size: 13px; background: #f8f9fa;">${device.device_id || 'N/A'}</td>
          </tr>
        `
      }).join('')
      
      const htmlContent = `
        <div style="text-align: left;">
          <div style="margin-bottom: 16px; padding: 12px 16px; background: linear-gradient(135deg, #5f9b46 0%, #4a7c3a 100%); border-radius: 8px; color: white; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 8px rgba(95, 155, 70, 0.3);">
            <div style="display: flex; align-items: center; gap: 10px;">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink: 0;">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
              </svg>
              <span style="font-weight: 600; font-size: 16px;">${workingDevices.length} Working Device${workingDevices.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          
          <div style="background: #fff; border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1); margin-bottom: 16px;">
            <div style="overflow-x: auto; max-height: 400px; overflow-y: auto;">
              <table style="width: 100%; border-collapse: collapse; min-width: 100%;">
                <thead style="position: sticky; top: 0; z-index: 10; background: #f8f9fa;">
                  <tr style="border-bottom: 2px solid #dee2e6;">
                    <th style="padding: 12px 8px; text-align: center; font-weight: 600; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; width: 50px; background: #f8f9fa;">#</th>
                    <th style="padding: 12px 16px; text-align: left; font-weight: 600; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; background: #f8f9fa;">Room Name</th>
                    <th style="padding: 12px 16px; text-align: left; font-weight: 600; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; background: #f8f9fa;">Device ID</th>
                  </tr>
                </thead>
                <tbody id="working-devices-list">
                  ${tableRows}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      `

      Swal.fire({
        icon: 'success',
        title: this.$t('working_devices') || 'Working Devices',
        html: htmlContent,
        width: '700px',
        confirmButtonText: this.$t('close') || 'Close',
        confirmButtonColor: '#3085d6',
        customClass: {
          popup: 'working-devices-modal'
        },
        didOpen: () => {
          const deviceRows = document.querySelectorAll('#working-devices-list .device-row')
          
          // Add hover effects to table rows
          deviceRows.forEach((row, index) => {
            const isEven = index % 2 === 0
            const defaultBg = isEven ? '#fff' : '#fafafa'
            row.addEventListener('mouseenter', function() {
              this.style.background = '#e8f5e9'
              const cells = this.querySelectorAll('td')
              cells.forEach(cell => {
                if (cell.style.background === 'rgb(248, 249, 250)') {
                  cell.style.background = '#e8f5e9'
                }
              })
            })
            row.addEventListener('mouseleave', function() {
              this.style.background = defaultBg
              const cells = this.querySelectorAll('td')
              cells.forEach(cell => {
                if (cell.style.background === 'rgb(232, 245, 233)') {
                  cell.style.background = '#f8f9fa'
                }
              })
            })
          })
        }
      })
    },
    showOfflineDevices() {
      const offlineDevices = this.getOfflineDevices()
      
      if (offlineDevices.length === 0) {
        Swal.fire({
          icon: 'info',
          title: this.$t('offline'),
          text: this.$t('no_offline_devices') || 'No offline devices found',
          confirmButtonColor: '#3085d6'
        })
        return
      }

      // Create table rows
      const tableRows = offlineDevices.map((device, index) => {
        const isEven = index % 2 === 0
        return `
          <tr class="device-row" style="border-bottom: 1px solid #e8e8e8; transition: background 0.2s; background: ${isEven ? '#fff' : '#fafafa'};">
            <td style="padding: 14px 8px; text-align: center; color: #666; font-weight: 500; width: 50px; font-size: 13px;">${index + 1}</td>
            <td style="padding: 14px 16px; font-weight: 500; color: #2c3e50; font-size: 14px;">${device.room_name || 'N/A'}</td>
            <td style="padding: 14px 16px; color: #555; font-family: 'Courier New', monospace; font-size: 13px; background: #f8f9fa;">${device.device_id || 'N/A'}</td>
          </tr>
        `
      }).join('')
      
      const htmlContent = `
        <div style="text-align: left;">
          <div style="margin-bottom: 16px; padding: 12px 16px; background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%); border-radius: 8px; color: white; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 8px rgba(238, 90, 111, 0.3);">
            <div style="display: flex; align-items: center; gap: 10px;">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink: 0;">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" fill="currentColor"/>
              </svg>
              <span style="font-weight: 600; font-size: 16px;">${offlineDevices.length} Offline Device${offlineDevices.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          
          <div style="background: #fff; border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1); margin-bottom: 16px;">
            <div style="overflow-x: auto; max-height: 400px; overflow-y: auto;">
              <table style="width: 100%; border-collapse: collapse; min-width: 100%;">
                <thead style="position: sticky; top: 0; z-index: 10; background: #f8f9fa;">
                  <tr style="border-bottom: 2px solid #dee2e6;">
                    <th style="padding: 12px 8px; text-align: center; font-weight: 600; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; width: 50px; background: #f8f9fa;">#</th>
                    <th style="padding: 12px 16px; text-align: left; font-weight: 600; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; background: #f8f9fa;">Room Name</th>
                    <th style="padding: 12px 16px; text-align: left; font-weight: 600; color: #495057; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; background: #f8f9fa;">Device ID</th>
                  </tr>
                </thead>
                <tbody id="offline-devices-list">
                  ${tableRows}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      `

      Swal.fire({
        icon: 'info',
        title: this.$t('offline_devices') || 'Offline Devices',
        html: htmlContent,
        width: '700px',
        confirmButtonText: this.$t('close') || 'Close',
        confirmButtonColor: '#3085d6',
        customClass: {
          popup: 'offline-devices-modal'
        },
        didOpen: () => {
          const deviceRows = document.querySelectorAll('.device-row')
          
          // Add hover effects to table rows
          deviceRows.forEach((row, index) => {
            const isEven = index % 2 === 0
            const defaultBg = isEven ? '#fff' : '#fafafa'
            row.addEventListener('mouseenter', function() {
              this.style.background = '#e3f2fd'
              const cells = this.querySelectorAll('td')
              cells.forEach(cell => {
                if (cell.style.background === 'rgb(248, 249, 250)') {
                  cell.style.background = '#e3f2fd'
                }
              })
            })
            row.addEventListener('mouseleave', function() {
              this.style.background = defaultBg
              const cells = this.querySelectorAll('td')
              cells.forEach(cell => {
                if (cell.style.background === 'rgb(227, 242, 253)') {
                  cell.style.background = '#f8f9fa'
                }
              })
            })
          })
        }
      })
    },
  },
  computed: {
    ...mapGetters(["loggedUser", 'filterSelected', 'isRTL']),
    canShowScanColumn() {
      return canShowPadScanColumn(this.loggedUser?.type, this.devicesStatus)
    },
    canShowActionsColumn() {
      return canSeeDeviceActions(this.loggedUser)
    },
    devicesToShow() {
      const { devicesStatus, searchTxt, searchBy } = this
      if (!devicesStatus || !devicesStatus.length) return []

      const fieldsToOmit = ['site_id', 'room_id', 'building', 'floor']
      if (!this.canShowActionsColumn) fieldsToOmit.push('actions')

      let devicesToShow = devicesStatus.map((device) => {
        const row = { ...device }
        fieldsToOmit.forEach((field) => delete row[field])
        row.device_type = this.$t(device.device_type.toLowerCase())
        return appendScanDeviceCell(
          row,
          device,
          this.canShowScanColumn,
          this.$t('scan_or_enter_device_code'),
        )
      })

      if (searchTxt) {
        devicesToShow = util.filterArrayOfObjectsWithString(devicesToShow, searchTxt, searchBy)
      }
      return devicesToShow
    },
    sortComparators() {
      const locationById = new Map(
        (this.devicesStatus || []).map(({ id, building, floor }) => [id, { building, floor }])
      )
      return {
        room_name: (a, b) => compareRoomLocation(
          { ...locationById.get(a.id), room_name: a.room_name },
          { ...locationById.get(b.id), room_name: b.room_name },
        ),
      }
    },
    deviceStats() {
      // Calculate stats directly - caching is handled by the watcher
      return this.calculateAllStats()
    },
    totalDevices() {
      return this.deviceStats.total
    },
    workingDevices() {
      return this.deviceStats.working
    },
    warningDevices() {
      return this.deviceStats.warning
    },
    offlineDevices() {
      return this.deviceStats.offline
    },
    workingPercentage() {
      if (this.totalDevices === 0) return 0
      return Math.round((this.workingDevices / this.totalDevices) * 100)
    },
    warningPercentage() {
      if (this.totalDevices === 0) return 0
      return Math.round((this.warningDevices / this.totalDevices) * 100)
    },
    offlinePercentage() {
      if (this.totalDevices === 0) return 0
      return Math.round((this.offlineDevices / this.totalDevices) * 100)
    },
    gridStyle() {
      return getDeviceStatusGridStyle(this.isMobile, this.canShowScanColumn, this.canShowActionsColumn)
    },
  },
  watch: {
    devicesStatus: {
      handler(newVal, oldVal) {
        // Update site action flags and table titles when devices are refreshed
        if (newVal !== oldVal && newVal && newVal.length > 0) {
          this.setSiteActionFlags()
          this.setTableTitles()
        }
      },
      immediate: false
    }
  },
  components: { 
    Table, 
    TableSearch,
    DeviceActionsDropdown,
    DeviceStatusStats
  }
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.device-status-container {
  width: 100%;
  min-height: 54vh;
  background-color: $white;
  box-shadow: 0 0 1px #bdbdbd;
  overflow-x: hidden;

  &.has-scan-column {
    padding-bottom: 96px;
  }

  @media (max-width: 820px) {
    min-height: auto;
    overflow-x: visible;

    &.has-scan-column {
      padding-bottom: 120px;
    }
  }

  .header {
    padding: 20px 25px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    background: $white;
    border-bottom: 1px solid $grey-border-color;
    position: sticky;
    top: 0;
    z-index: 10;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);

    @media (max-width: 820px) {
      z-index: 1000;
      pointer-events: auto;
    }

    @media (max-width: 820px) {
      flex-direction: column;
      align-items: flex-start;
      padding: 15px;
      gap: 15px;
    }

    @media (max-width: 480px) {
      padding: 12px;
      gap: 12px;
    }

    @media (max-width: 820px) {
      flex-direction: column;
      align-items: flex-start;
      padding: 15px;
    }

    .header-left {
      display: flex;
      align-items: center;
      flex-shrink: 0;

      @media (max-width: 480px) {
        width: 100%;
      }

      h4 {
        font-size: 20px;
        font-weight: $font-weight-bold;
        color: $black-blue;
        margin: 0;

        @media (max-width: 820px) {
          font-size: 18px;
        }

        @media (max-width: 480px) {
          font-size: 16px;
        }
      }
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 20px;
      flex: 1;
      justify-content: flex-end;

      @media (max-width: 820px) {
        width: 100%;
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
      }

      @media (max-width: 480px) {
        gap: 10px;
      }

      @media (max-width: 820px) {
        width: 100%;
        flex-direction: column;
        align-items: stretch;
        gap: 15px;
      }
    }

    .search-container-wrapper {
      flex: 0 0 auto;
      min-width: 280px;
      position: relative;
      z-index: 1;

      @media (max-width: 1200px) {
        min-width: 240px;
      }

      @media (max-width: 820px) {
        width: 100%;
        min-width: 100%;
        order: -1;
        z-index: 1;
      }
    }
  }

  .shift-params-table {
    margin: 0 auto;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    ::v-deep .table-cell.icon img[alt="qr_code"] {
      width: 18px;
      height: 18px;
      max-width: 18px;
      max-height: 18px;
      object-fit: contain;
      display: block;
      margin: 0 auto;
    }

    @media (max-width: 1200px) {
      width: 100%;
    }

    @media (max-width: 820px) {
      width: 100%;
      margin: 0;
      padding: 0 10px;
    }

    @media (max-width: 480px) {
      padding: 0 5px;
    }

    // Removed hover tooltips - labels are always visible on mobile via ::before

    // Mobile table styles - convert to card layout
    @media (max-width: 820px) {
      ::v-deep .table-container {
        margin-inline-start: 0;
        padding: 0;
        box-shadow: none;
        border-radius: 0;
        background: transparent;
      }

      ::v-deep .table-head {
        display: none;
      }

      ::v-deep .table-body {
        display: flex;
        flex-direction: column;
        gap: 12px;
        overflow: visible;
      }

      ::v-deep .table-row {
        display: block !important;
        background: $white;
        border: 1px solid $grey-border-color;
        border-radius: 8px;
        padding: 12px;
        margin-bottom: 0;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        height: auto !important;
        grid-template-columns: 1fr !important;
        position: relative;
        overflow: visible;
        transition: all 0.3s ease;
        cursor: pointer;

        &:hover {
          background-color: #f8f9fa;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        // Card header styling
        .mobile-card-header {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          padding: 12px 15px;
          border-bottom: 1px solid #f0f0f0;
          background: #fafafa;
          
          .mobile-expand-arrow {
            font-size: 18px;
            color: #666;
            cursor: pointer;
            user-select: none;
            z-index: 10;
            transition: transform 0.3s ease;
            padding: 4px 8px;
            
            &:active {
              opacity: 0.7;
            }
          }
        }
        
        // Default to collapsed state - hide all cells except first
        &:not(.mobile-expanded) {
          .table-cell:not(.cell-icon):not(.checkbox-container):not(.triangle-container):not(:first-child) {
            display: none !important;
          }
        }

        // Expanded state - show all cells (including icon cells)
        &.mobile-expanded {
          .table-cell:not(.checkbox-container):not(.triangle-container) {
            display: flex !important;
          }
          
          // Override mobile-hidden when expanded
          .table-cell.mobile-hidden {
            display: flex !important;
          }
        }

        .table-cell {
          display: flex !important;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #f0f0f0;
          width: 100% !important;
          grid-column-start: 1 !important;
          transition: opacity 0.3s ease;
          
          @include rtl {
            flex-direction: row-reverse; // Reverse flex direction in RTL
          }
          
          // Allow tooltips to overflow on mobile (status, version, battery, etc.)
          &.version-cell,
          &.icon-cell,
          &:has(.tooltip) {
            overflow: visible !important;
          }

          ::v-deep .tooltip {
            overflow: visible;
          }

          &:last-child {
            border-bottom: none;
          }

          // Hide cells when collapsed (except first one)
          &.mobile-hidden {
            display: none !important;
          }

          // Expand arrow styling
          .mobile-expand-arrow {
            position: absolute;
            left: 15px;
            top: 50%;
            transform: translateY(-50%);
            font-size: 16px;
            color: #666;
            cursor: pointer;
            user-select: none;
            z-index: 10;
            transition: transform 0.3s ease;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            
            &:active {
              opacity: 0.7;
            }
          }
          
          // Summary cell styling (first cell when collapsed)
          &.mobile-summary-cell {
            border-bottom: none !important;
            padding-bottom: 0 !important;
            padding-top: 8px !important;
            padding-left: 50px !important; // Make room for arrow
            font-weight: 600;
            font-size: 16px;
            color: $black-blue;
            display: flex !important;
            position: relative !important;
            
            // Hide the label (::before) when showing as summary
            &::before {
              display: none !important;
              content: none !important;
            }
            
            // Expand arrow styling
            .mobile-expand-arrow {
              position: absolute;
              left: 15px;
              top: 50%;
              transform: translateY(-50%);
              font-size: 16px;
              color: #666;
              cursor: pointer;
              user-select: none;
              z-index: 10;
              transition: transform 0.3s ease;
              width: 24px;
              height: 24px;
              display: flex;
              align-items: center;
              justify-content: center;
              
              &:active {
                opacity: 0.7;
              }
            }
            
            // Make the content more prominent
            span {
              font-weight: 600;
              font-size: 16px;
              color: $black-blue;
            }
          }

          &:before {
            content: attr(data-label);
            font-weight: 600;
            color: #64748b;
            font-size: 11px;
            text-transform: capitalize;
            letter-spacing: 0.3px;
            min-width: 100px;
            flex-shrink: 0;
            margin-right: 12px;
            display: inline-block;
            
            @include rtl {
              margin-right: 0;
              margin-left: 12px;
              order: 2; // Move label to the right in RTL
            }
          }

          // Show label even if data-label is not set (fallback)
          &:not([data-label]):not(:empty) {
            &::before {
              content: attr(data-label);
            }
          }
          
          &.text-with-icon-cell {
            flex-wrap: nowrap;
            
            &:before {
              width: auto;
              margin-bottom: 0;
            }
            
            img {
              margin-left: auto;
            }
          }
          
          // Version should display inline
          &[data-label*="Version"],
          &[data-label*="version"],
          &.version-cell {
            span {
              display: inline;
            }
            
            // Allow tooltip to overflow on mobile
            @media (max-width: 820px) {
              overflow: visible !important;
              
              .merged-icon-wrapper.tooltip {
                overflow: visible !important;
              }
            }
          }

          // Battery cell styling on mobile - make it larger and more visible
          &.battery-cell {
            .battery-container {
              width: 80px !important;
              height: 32px !important;
              min-width: 80px;
              display: flex;
              align-items: center;
              gap: 0;
              
              .body {
                padding: 5px 6px;
                border-width: 3.5px !important;
                border-radius: 4px;
                width: 100%;
                min-height: 24px;
                
                span {
                  font-size: 12px !important;
                  font-weight: 700;
                  line-height: 1.2;
                  letter-spacing: 0.3px;
                }
              }
              
              // Hide the tip (tail) completely on mobile
              .tip {
                display: none !important;
              }
            }
          }

          span {
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: normal;
            word-break: break-word;
            flex: 1;
            text-align: right;
            
            @include rtl {
              text-align: left;
              order: 1; // Move content to the left in RTL
            }
          }

          // Icons that are merged into text cells
          img {
            max-width: 24px;
            max-height: 24px;
            margin-left: auto;
            flex-shrink: 0;
            
            @include rtl {
              margin-left: 0;
              margin-right: auto;
            }
          }
          
          // Merged icon wrapper positioning
          .merged-icon-wrapper {
            margin-left: auto;
            
            @include rtl {
              margin-left: 0;
              margin-right: auto;
            }
          }
          
          // Tooltip styling for merged icons
          .merged-icon-wrapper.tooltip {
            position: relative;
            
            .mobile-tooltip.top {
              padding: 10px;
              color: #2c3e50;
              background-color: #f5f5f5;
              font-weight: normal;
              font-size: 12px;
              border-radius: 8px;
              position: absolute;
              box-sizing: border-box;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
              z-index: 9998; // Below modal (9999)
              white-space: pre-line;
              word-break: break-word;
              max-width: min(320px, calc(100vw - 32px));
              pointer-events: none;
              // On mobile, hide by default, show on hover/click (controlled by JS)
              // On desktop, hide by default and show on hover
              display: none;
              visibility: hidden;
              opacity: 0;
              
              // Mobile: show below icon, aligned to the right (matches card row layout)
              @media (max-width: 820px) {
                top: calc(100% + 8px);
                bottom: auto;
                left: auto;
                right: 0;
                min-width: 0;
                transform: none;

                i {
                  top: -15px;
                  left: auto;
                  right: 12px;
                  margin-left: 0;
                }
              }
              
              // Desktop positioning (top of icon, above)
              @media (min-width: 821px) {
                top: -10px;
                left: 50%;
                min-width: max-content;
                transform: translate(-50%, -100%);
                
                @include rtl {
                  left: auto;
                  right: 50%;
                  transform: translate(50%, -100%);
                }
              }
              
              i {
                position: absolute;
                top: 100%;
                left: 50%;
                margin-left: -15px;
                width: 30px;
                height: 15px;
                overflow: hidden;
                pointer-events: none;
                
                div {
                  position: absolute;
                  width: 15px;
                  height: 15px;
                  left: 50%;
                  top: 0;
                  transform: translate(-50%, -50%) rotate(45deg);
                  background-color: #f5f5f5;
                  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.3);
                }
              }
            }
            
            // Desktop: hide by default, show on hover
            @media (min-width: 821px) {
              .mobile-tooltip.top {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
              }
              
              &:hover .mobile-tooltip.top {
                display: block !important;
                visibility: visible !important;
                opacity: 1 !important;
              }
            }
            
          }
          
          // Version should be inline
          &.version-cell {
            span {
              display: inline;
              text-align: left;
            }
          }
        }

        // Hide row number on mobile
        .table-cell:last-child {
          display: none !important;
        }
        
        // Hide latest errors row completely on mobile (just like row number)
        // Hide both the cell and its label
        .table-cell[data-label*="error" i],
        .table-cell[data-label*="Error"],
        .table-cell[data-label*="latest" i],
        .table-cell[data-label*="Latest"] {
          display: none !important;
          
          // Also hide the label (::before pseudo-element)
          &::before {
            display: none !important;
            content: none !important;
          }
        }
        
        // Also hide any cell containing errors_list icon (handled via JS, but CSS fallback)
        .table-cell img[src*="errors_list"] {
          display: none !important;
        }
        
        // Hide cells that have latest_errors in their content
        .table-cell:has(img[src*="errors_list"]) {
          display: none !important;
          
          &::before {
            display: none !important;
            content: none !important;
          }
        }

        // Hide standalone icon cells - they'll be merged with previous cells
        // Hide latest_errors on mobile
        .table-cell.icon-cell {
          // Hide latest errors icon cell on mobile
          &[data-label*="error"],
          &[data-label*="Error"],
          &[data-label*="latest"],
          &[data-label*="Latest"] {
            display: none !important;
          }
          
          // If it has a data-label and is not latest errors, show it as a row first, then it will be merged
          &[data-label]:not([data-label*="error"]):not([data-label*="Error"]):not([data-label*="latest"]):not([data-label*="Latest"]) {
            display: flex !important;
            
            &:before {
              content: attr(data-label);
              font-weight: 600;
              color: #64748b;
              font-size: 11px;
              text-transform: capitalize;
              letter-spacing: 0.3px;
              min-width: 100px;
              flex-shrink: 0;
              margin-right: 12px;
            }
          }
        }
        
        // Also hide any cell that contains latest_errors icon
        .table-cell:has(img[src*="errors_list"]) {
          display: none !important;
        }
        
        // Don't merge icons - show them in their own cells like desktop
        // Icon cells are already handled above to show as separate rows
        
        .cell-icon {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #f0f0f0;
          width: 100% !important;

          &:before {
            content: attr(data-label);
            font-weight: 600;
            color: #64748b;
            font-size: 11px;
            text-transform: capitalize;
            letter-spacing: 0.3px;
            min-width: 100px;
            flex-shrink: 0;
            margin-right: 12px;
          }

          &.action {
            visibility: visible !important;
            flex-direction: row;
            align-items: center;
            gap: 10px;
            padding: 10px;
            background: #f8f9fa;
            border-radius: 6px;
            margin-top: 8px;
            
            // Make action buttons more recognizable with labels
            img {
              width: 32px;
              height: 32px;
              padding: 6px;
              border-radius: 6px;
              background: $white;
              border: 1px solid #e0e0e0;
              transition: all 0.2s;
              flex-shrink: 0;
              
              &:active {
                background: #e0e0e0;
                transform: scale(0.95);
              }
            }
            
            // Show label text (from JavaScript-added element or ::after)
            .action-label-text {
              font-size: 13px;
              color: #2c3e50;
              font-weight: 500;
              white-space: nowrap;
              flex: 1;
            }
            
            // Fallback: Add text label via CSS if JS didn't add it
            &::after {
              content: attr(data-label);
              font-size: 13px;
              color: #2c3e50;
              font-weight: 500;
              white-space: nowrap;
              flex: 1;
              margin-left: 8px;
            }
            
            // Hide ::after if has-label-text class is present (meaning JS added the label)
            &.has-label-text::after {
              display: none;
            }
          }
        }
        
        // Group actions in a dedicated section
        .table-row {
          // Find the actions container if it exists
          .actions-group {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            padding: 8px 0;
            border-top: 1px solid #e0e0e0;
            margin-top: 8px;
            
            .cell-icon.action {
              flex: 0 0 auto;
              width: auto;
              padding: 6px 10px;
              border: 1px solid #e0e0e0;
              border-radius: 6px;
              background: #fafafa;
              
              &:before {
                display: none;
              }
            }
          }
        }
      }
    }

    @media (max-width: 480px) {
      ::v-deep .table-row {
        padding: 10px;

        .table-cell {
          padding: 6px 0;
          font-size: 13px;

          &:before {
            font-size: 10px;
            min-width: 80px;
            margin-right: 8px;
          }
        }
      }
    }
  }
}

// Global styles for offline devices modal
::v-deep .offline-devices-modal {
  .swal2-popup {
    padding: 0;
  }

  .swal2-title {
    padding: 20px 24px 16px;
    margin: 0;
    border-bottom: 1px solid #e0e0e0;
  }

  .swal2-html-container {
    padding: 0 24px 24px;
    margin: 0;
  }

  .swal2-actions {
    padding: 0 24px 24px;
    margin: 0;
  }
}

// Global styles for working devices modal
::v-deep .working-devices-modal {
  .swal2-popup {
    padding: 0;
  }

  .swal2-title {
    padding: 20px 24px 16px;
    margin: 0;
    border-bottom: 1px solid #e0e0e0;
  }

  .swal2-html-container {
    padding: 0 24px 24px;
    margin: 0;
  }

  .swal2-actions {
    padding: 0 24px 24px;
    margin: 0;
  }
}
</style>
